import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ChatTextType,
  MuteSettingsType,
  UpdateConversationSettingsParams,
} from "../../../../libraries/types_interface/chats/ChatsTypes";
import CacheStorage from "../../../../service/storage/CacheStorage";
import StorageKeys from "../../../../const/StorageKeys";
import {
  GroupConversationMessageModel,
  Message,
} from "../../../../model/chats/GroupConversationMessageModel";
import SocketService, {
  socket,
} from "../../../../service/socket/SocketService";
import SessionStorage from "../../../../service/storage/SessionStorage";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import { ConversationList } from "../../../../model/chats/ChatListModel";
import SocketChatsKeys from "../../../../const/SocketChatsKeys";
import EncryptDecryptService, {
  GroupMessageEncryptedType,
  HybridEncrypted,
} from "../../../../service/encrypt_decrypt/EncryptDecryptService";
import GroupChatsProvider from "./GroupChatsProvider";
import {
  GroupConversationDetailsModel,
  GroupMutedConversations,
  GroupResults,
} from "../../../../model/chats/GroupConversationDetailsModel";
import { useDispatch } from "react-redux";
import { ResponsiveDeviceType } from "../../../../widget/layouts/ScreenMedia";
import ChatsProvider from "../chats/ChatsProvider";
import {
  chatRefreshListStatus,
  clearResetSelectedChat,
  mediaDeleteChooseAction,
  sendForwardMessages,
  setForwardMediaList,
  updateGroupConversationDetail,
} from "../../../../libraries/state_reducers/chats/ChatsReducer";
import {
  addModal,
  removeModal,
} from "../../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../../const/ModalKeys";
import { toLocalDate } from "../../../../service/date_service/date_service";
import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import { AlertConfirm } from "../../../../widget/common/ui/Alerts";
import { appRoutes } from "../../../../routes/routes";
import { navigate } from "../../../../service/navigation/Navigation";
import Loader from "../../../../widget/common/ui/Loader";
import { UploadEncryptedMedia } from "../../../../model/chats/ChatMediaUploadedDetailModel";
import ImagePickerPreviewProvider from "../../../widget/ImagePickerPreviewProvider";
import {
  audioExtensions,
  docExtensions,
  imageExtensions,
  pdfExtensions,
  videoExtensions,
} from "../../../../const/FileExtensions";
import { isMobile } from "react-device-detect";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import { ForwardMessageList } from "../../../../model/chats/ForwardProfileListModel";

type GroupChatsContextType = {
  getSelectedChatSession: () => void;
  resetMessages: () => void;
  conversationCallBack: (eventName: string, ...args: any[]) => void;
  disconnectCallBacks: () => void;
  updateConversationSettings: (
    detail: UpdateConversationSettingsParams
  ) => void;
  submitGroupExit: (exitType: "1" | "2") => void;

  userId: string;
  conversationMessages: Record<string, Array<Message>> | null;
  conversationDetail: GroupResults | null;
  muteSettings: MuteSettingsType;

  groupMembers: Array<string>;
  groupMembersOnline: Array<boolean>;

  removedMember: boolean;

  onlineStatus: boolean;
  typingStatus: boolean;
  recordingStatus: boolean;

  // CHATTING
  sendTextMessage: (msg: string, callback: () => void) => void;
  sendAudioMessage: (
    file: File,
    secondsDuration: number,
    callback: () => void
  ) => void;
  sendMediaMessage: (
    file: File,
    secondsDuration: number,
    callback: () => void
  ) => void;

  // OPTION
  handleClickMessage: (message: Message) => void;
  handlePressStart: (message: Message) => void;
  handlePressEnd: () => void;
  clearLongPress: () => void;
  selectedMesasgeIds: Array<string>;
  selectedMessages: Array<Message>;
  longPressStatus: boolean;

  longPressAction: (action: "reply" | "copy" | "delete" | "forward") => void;
};

const GroupChatsContext = createContext<GroupChatsContextType | undefined>(
  undefined
);

export const GroupChatsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const muteDurationGroupSelector = useSelector(
    (state: RootState) => state.chats.muteDurationGroup
  );

  const mediaDeleteActionTypeSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteActionType
  );

  const mediaDeleteEveryoneSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteEveryone
  );

  const forwardedMediaListSelector = useSelector(
    (state: RootState) => state.chats.forwardedMediaList
  );

  const forwardedMessagesSelector = useSelector(
    (state: RootState) => state.chats.forwardedMessages
  );

  const voiceLengthRef = useRef<string>("00:00");

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressStatusRef = useRef<boolean>(false);
  const selectedMesasgeIdsRef = useRef<Array<string>>([]);

  const userId = CacheStorage.getValue(StorageKeys.account_id) || "0";
  const privateKeyRef = useRef<string>(
    SessionStorage.getValue(StorageKeys.chats_symetric_key) || ""
  );
  const chatSelectedRef = useRef<ConversationList | null>(null);
  const conversationDetailRef = useRef<GroupResults | null>(null);
  const conversationMessagesRef = useRef<Array<Message> | null>(null);
  const groupMembersRef = useRef<Array<string>>([]);
  const groupMembersOnlineRef = useRef<Array<boolean>>([]);

  const muteSettingsRef = useRef<MuteSettingsType>(
    ChatsProvider.muteSettingsDefault
  );

  const [groupMembers, setGroupMembers] = useState<Array<string>>([]);
  const [groupMembersOnline, setGroupMembersOnline] = useState<Array<boolean>>(
    []
  );

  const [removedMember, setRemovedMember] = useState<boolean>(false);
  const [onlineStatus, setOnlineStatus] = useState<boolean>(false);
  const [typingStatus, setTypingStatus] = useState<boolean>(false);
  const [recordingStatus, setRecordingStatus] = useState<boolean>(false);

  const [conversationDetail, setConversationDetail] =
    useState<GroupResults | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Record<
    string,
    Message[]
  > | null>(null);

  const [muteSettings, setMuteSettings] = useState<MuteSettingsType>(
    ChatsProvider.muteSettingsDefault
  );

  const [longPressStatus, setLongPressStatus] = useState<boolean>(false);
  const [selectedMesasgeIds, setSelectedMesasgeIds] = useState<Array<string>>(
    []
  );
  const [selectedMessages, setSelectedMessages] = useState<Array<Message>>([]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const resetValues = () => {
    privateKeyRef.current =
      SessionStorage.getValue(StorageKeys.chats_symetric_key) || "";
    chatSelectedRef.current = null;
    groupMembersRef.current = [];
    groupMembersOnlineRef.current = [];

    setRemovedMember(false);
    setOnlineStatus(false);
    setTypingStatus(false);
    setRecordingStatus(false);

    setGroupMembers([]);
    setGroupMembersOnline([]);

    muteSettingsRef.current = ChatsProvider.muteSettingsDefault;
    setMuteSettings(ChatsProvider.muteSettingsDefault);

    resetMessages();
  };

  const clearLongPress = () => {
    longPressStatusRef.current = false;
    setLongPressStatus(false);

    setSelectedMesasgeIds([]);
    selectedMesasgeIdsRef.current = [];
    setSelectedMessages([]);
  };

  const resetMessages = () => {
    conversationDetailRef.current =
      ChatsProvider.getChatConversationDetailSession();
    conversationMessagesRef.current = null;

    setConversationDetail(conversationDetailRef.current);
    setConversationMessages(conversationMessagesRef.current);

    clearLongPress();
  };

  // GET SESSION SELECTED CHAT -> getting messages ////////////////////////////////////////////
  const getSelectedChatSession = async (
    getConversDetailStatus: boolean = true
  ) => {
    if (socket.connected) {
      const chatsChatSelectedSession = SessionStorage.getValue(
        StorageKeys.chats_chat_selected
      );

      if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
        const chatSelected: ConversationList = JSON.parse(
          chatsChatSelectedSession
        );

        chatSelectedRef.current = chatSelected;

        if (getConversDetailStatus) {
          SocketService.initConversation({
            flag: chatSelected?.flag!,
            eventN:
              chatSelected?.flag! === "3"
                ? SocketChatsKeys.join_chat_group
                : SocketChatsKeys.join_chat,
            convId: chatSelected?.conv_id!,
            //socketCallBack: conversationCallBack,
            callback: (response: any) => {
              const groupConversationDetailsModel: GroupConversationDetailsModel =
                response;

              const symetricKey: string =
                groupConversationDetailsModel?.results?.symmetric_key!;

              const groupMembers: Array<string> =
                groupConversationDetailsModel.results?.members_id != null
                  ? groupConversationDetailsModel.results?.members_id?.split(
                      ","
                    )
                  : [];

              let groupMembersOnline: Array<boolean> = [];

              for (var element in groupMembers) {
                groupMembersOnline.push(element === userId);
              }

              groupMembersRef.current = groupMembers;
              groupMembersOnlineRef.current = groupMembersOnline;

              conversationDetailRef.current =
                groupConversationDetailsModel?.results!;
              privateKeyRef.current = symetricKey;

              if (conversationDetailRef.current?.members! !== undefined) {
                const removedMemberTemp: boolean =
                  conversationDetailRef.current?.members!.some(
                    (element) =>
                      element.account_id === userId && element.status === "0"
                  ) ?? false;

                setRemovedMember(removedMemberTemp);
              }

              setGroupMembers(groupMembers);
              setGroupMembersOnline(groupMembersOnline);

              setConversationDetail(conversationDetailRef.current);

              console.log(conversationDetailRef.current);

              SessionStorage.saveValue(
                StorageKeys.chats_symetric_key,
                symetricKey
              );

              ChatsProvider.setupChatConversationDetail({
                flag: chatSelectedRef.current?.flag!,
                detail: conversationDetailRef.current,
              });

              updateMuteSettings();
            },
          });
        }
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateMuteSettings = () => {
    const mutedConversations: GroupMutedConversations =
      conversationDetailRef.current?.conv_settings?.muted_conversations!;
    muteSettingsRef.current = ChatsProvider.muteSettingsDefault;

    muteSettingsRef.current = {
      ...muteSettingsRef.current,
      muteStatus:
        conversationDetailRef.current?.conv_settings?.muted_conversations
          ?.mute_status!,
    };

    if (mutedConversations?.mute_status! === "1") {
      muteSettingsRef.current = {
        ...muteSettingsRef.current,
        muteDuration: mutedConversations?.mute_duration!,
        muteEnd:
          mutedConversations.mute_type! === "1"
            ? toLocalDate(
                new Date(mutedConversations?.mute_end!),
                "d MMMM, h:mm a",
                true
              )
            : "",
      };
    }

    setMuteSettings(muteSettingsRef.current);
  };

  const submitMuteDetail = (muteStatusVal: string) => {
    console.log(`submitMuteDetail: ${muteStatusVal}`);
    if (["1", "2", "3", "4", "5"].includes(muteStatusVal)) {
      const muteSettingsTemp: MuteSettingsType = ChatsProvider.updateMuteStatus(
        Number(muteStatusVal),
        muteSettingsRef.current
      );

      updateConversationSettings({
        convId: chatSelectedRef?.current?.conv_id!,
        eventName: SocketChatsKeys.chat_mute_notification,
        muteDuration: muteSettingsTemp?.muteDuration,
        muteStatus: muteSettingsTemp?.muteStatus,
        muteType: muteSettingsTemp?.muteType,
      });
    }
  };

  const submitGroupExit = (exitType: "1" | "2") => {
    AlertConfirm({
      title:
        exitType === "1"
          ? "are_you_sure_you_wish_to_exit_this_group"
          : "are_you_sure_you_wish_to_exit_and_delete_this_group",
      onConfirm: () => {
        // GET CHAT DETAIL
        getSelectedChatSession(false);
        updateConversationSettings({
          convId: chatSelectedRef?.current?.conv_id!,
          eventName: SocketChatsKeys.user_exit_chat_group,
          exitFlag: exitType,
        });
      },
    });
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateOnlineStatusOfMember = (acctId: string, status: boolean) => {
    if (groupMembersRef.current.includes(acctId)) {
      groupMembersOnlineRef.current[groupMembersRef.current.indexOf(acctId)] =
        status;
    }

    const onlineCount: number = groupMembersOnlineRef.current.map(
      (element) => element
    ).length;

    setOnlineStatus(onlineCount <= 1 ? false : true);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateDeleteStatusFromOther = (msgIds: string) => {
    const messagesIds: Array<string> = msgIds.split(",");
    let conversationMessagesTemp: Message[] = [
      ...conversationMessagesRef.current!,
    ];

    conversationMessagesTemp.map(async (element) => {
      if (messagesIds.includes(element?.message_id!)) {
        element.status = "0";
      }
    });

    conversationMessagesRef.current = conversationMessagesTemp;
    messageDecode(conversationMessagesTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const conversationCallBack = async (eventName: string, ...args: any[]) => {
    console.log(
      `ConversationCallBack Group: ${eventName} flag: ${chatSelectedRef.current
        ?.flag!}`,
      args
    );

    let hasConvId: boolean = false;
    let results: any = null;
    let resultConversationId: string = "";

    if (chatSelectedRef.current?.flag! === "3") {
      if (args.length > 0) {
        results = args[0]["results"] ?? null;
        hasConvId = results != null && "conv_id" in results;

        if (hasConvId) {
          resultConversationId = results["conv_id"];

          if (
            [
              SocketChatsKeys.profile_offline,
              SocketChatsKeys.profile_online,
            ].includes(eventName)
          ) {
            updateOnlineStatusOfMember(
              results["account_id"],
              eventName === SocketChatsKeys.profile_online
            );
          }

          if (UtilityHelper.isNotEmpty(resultConversationId)) {
            if (eventName === SocketChatsKeys.chat_group_message_history) {
              const groupConversationMessageModel: GroupConversationMessageModel =
                args[0];

              let conversationMessagesTemp: Message[] =
                groupConversationMessageModel?.results?.messages!;

              conversationMessagesTemp =
                conversationMessagesTemp.filter((msg) =>
                  msg.access_memebers?.split(",").includes(userId)
                ) ?? [];

              conversationMessagesRef.current = conversationMessagesTemp;
              messageDecode(conversationMessagesRef.current!);

              await UtilityHelper.delay();
              SocketService.readMessage(
                groupConversationMessageModel?.results?.conv_id!,
                chatSelectedRef?.current?.flag!
              );
              //
            } else if (eventName === SocketChatsKeys.new_chat_group_message) {
              if (
                "message" in results &&
                "conv_id" in results &&
                results["conv_id"] === chatSelectedRef.current?.conv_id
              ) {
                const message: Message = results["message"];

                if (message.access_memebers!.split(",").includes(userId)) {
                  //conversationMessagesRef.current!.push(message);
                  conversationMessagesRef.current = [
                    message,
                    ...conversationMessagesRef.current!,
                  ];
                  messageDecode(conversationMessagesRef.current!);

                  if (userId !== message.account_id) {
                    await UtilityHelper.delay();
                    SocketService.readMessage(
                      resultConversationId,
                      chatSelectedRef?.current?.flag!
                    );
                  }
                }

                // RELOAD ONLY IN WEB LAYOUT
                if (type === 3) {
                  dispatch(chatRefreshListStatus(true));
                }
              }
              //
            } else if (eventName === SocketChatsKeys.chat_delete_messages) {
              if ("message_id" in results) {
                updateDeleteStatusFromOther(results["message_id"]);
              }
              //
            }
          }
        }
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const messageDecode = async (messages: Array<Message>) => {
    if (conversationDetailRef.current != null) {
      let messagesList: Array<Message> = [];

      await Promise.all(
        messages!.map(async (element, index) => {
          if (
            element?.message! != null &&
            UtilityHelper.isNotEmpty(element.message!)
          ) {
            element.decripted_mesage = await EncryptDecryptService.decryptGroup(
              {
                secretKey: privateKeyRef.current,
                cipherTextBase64: element.message!,
                ivBase64: element.message_iv!,
                tagBase64: element.message_tag!,
              }
            );
          }

          if (
            element.replay_status === "1" &&
            element.replay_message != null &&
            UtilityHelper.isNotEmpty(element?.replay_message!.message ?? "")
          ) {
            element.replay_message!.decripted_mesage = "";

            element.replay_message!.decripted_mesage =
              await EncryptDecryptService.decryptGroup({
                secretKey: privateKeyRef.current,
                cipherTextBase64: element.replay_message!.message!,
                ivBase64: element.replay_message!.message_iv!,
                tagBase64: element.replay_message!.message_tag!,
              });
          }

          messagesList.push(element);
        })
      );

      messagesList = [...messagesList].sort(
        (a, b) => Number(b.message_id) - Number(a.message_id)
      );

      messagesList = messagesList.reverse();
      messagesList = GroupChatsProvider.checkPrevious(messagesList);
      messagesList = messagesList.reverse();

      let messagesListGrouped: Record<string, Message[]> =
        GroupChatsProvider.groupByDate(messagesList);

      messagesListGrouped = Object.fromEntries(
        Object.entries(messagesListGrouped).reverse()
      );

      /*  const sortedEntries = Object.entries(messagesListGrouped).sort(
        ([dateA], [dateB]) =>
          new Date(dateA).getTime() - new Date(dateB).getTime()
      );

      messagesListGrouped = {};
      sortedEntries.map((item) => {
        messagesListGrouped[item[0]] = item[1];
      }); */

      setConversationMessages(messagesListGrouped);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateConversationSettings = async ({
    convId,
    eventName,
    exitFlag = "",
    muteStatus = "",
    muteDuration = "",
    muteType = "",
    profileId = "",
    blockStatus = "",
  }: UpdateConversationSettingsParams) => {
    console.log(`convId: ${convId}, eventName: ${eventName}`);

    if (
      UtilityHelper.isNotEmpty(convId) &&
      UtilityHelper.isNotEmpty(eventName)
    ) {
      if (eventName === SocketChatsKeys.chat_clear_messages) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_clear_messages,
          callback: updateClearChatDate,
        });
      } else if (eventName === SocketChatsKeys.user_exit_chat_group) {
        await SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          exitFlag: exitFlag,
          eventN: SocketChatsKeys.user_exit_chat_group,
          callback: async (response: any) => {
            conversationDetailRef.current = {
              ...conversationDetailRef.current,
              status: "0",
            };

            setConversationDetail(conversationDetailRef.current);

            dispatch(
              updateGroupConversationDetail(conversationDetailRef.current)
            );

            await UtilityHelper.delay(200);
            dispatch(removeModal(ModalKeys.chat_group_exit));
          },
        });

        if (exitFlag === "2") {
          // REMOVE CHAT DETAIL SESSION
          SessionStorage.deleteValue(StorageKeys.chats_chat_selected);

          if (type === 3) {
            dispatch(chatRefreshListStatus(true));
            dispatch(clearResetSelectedChat(true));
          } else {
            navigate(appRoutes.routeNameChats, { replace: true });
          }

          await UtilityHelper.delay(200);
          dispatch(removeModal(ModalKeys.chat_group_exit));
        }
      } else if (eventName === SocketChatsKeys.chat_mute_notification) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_mute_notification,
          muteDuration: muteDuration,
          muteStatus: muteStatus,
          muteType: muteType,
          callback: async (response: any) => {
            conversationDetailRef.current = {
              ...conversationDetailRef.current,
              conv_settings: {
                ...conversationDetailRef.current?.conv_settings,
                muted_conversations: {
                  ...conversationDetailRef.current?.conv_settings
                    ?.muted_conversations!,
                  mute_status: response["results"]["mute_status"],
                  mute_type: response["results"]["mute_type"],
                  mute_start: response["results"]["mute_start"],
                  mute_duration: response["results"]["mute_duration"],
                  mute_end: response["results"]["mute_end"],
                },
              },
            };

            setConversationDetail(conversationDetailRef.current);
            updateMuteSettings();

            // RELOAD ONLY IN WEB LAYOUT
            if (type === 3) {
              dispatch(chatRefreshListStatus(true));
            }

            await UtilityHelper.delay(200);
            dispatch(removeModal(ModalKeys.chat_mute_notification));
          },
        });
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateClearChatDate = (response: any) => {
    const clearChatDateVal: string =
      response["results"]["clear_chat_date"] ?? "";

    if (UtilityHelper.isNotEmpty(clearChatDateVal)) {
      if (
        conversationDetailRef.current?.conv_settings?.clear_chat_date != null
      ) {
        conversationDetailRef.current = {
          ...conversationDetailRef.current,
          conv_settings: {
            ...conversationDetailRef.current?.conv_settings,
            clear_chat_date: clearChatDateVal,
          },
        };

        setConversationDetail(conversationDetailRef.current);

        conversationMessagesRef.current = [];
        setConversationMessages({});
      }
    }
  };

  /// SEND MESSAGE  //////////////////////////////////////////////////////////////////////////

  const sendTextMessage = (textMessage: string, callback: () => void) => {
    console.log(`sendTextMessage`);
    if (socket.connected && UtilityHelper.isNotEmpty(textMessage)) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      SocketService.typingChatMessage({
        convId: chatSelectedRef.current?.conv_id!,
        flag: "1",
        status: "0",
      });

      const txtType: ChatTextType = ChatsProvider.identifyTextType(textMessage);

      sendMessage({
        msg: textMessage,
        msgType: txtType === ChatTextType.url ? "9" : "1",
        callback: () => {
          callback();
        },
      });
    }
  };

  const sendAudioMessage = async (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => {
    console.log(`sendAudioMessage`);
    if (socket.connected) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      voiceLengthRef.current = UtilityHelper.formatTimer(
        Math.ceil(durationSeconds)
      );

      SocketService.typingChatMessage({
        convId: chatSelectedRef.current?.conv_id!,
        flag: "2",
        status: "0",
      });

      sendMediaMessage(file, durationSeconds, callback);
    }
  };

  const sendMediaMessage = async (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => {
    console.log(`sendMediaMessage`);
    if (socket.connected) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      voiceLengthRef.current = UtilityHelper.formatTimer(
        Math.ceil(durationSeconds)
      );

      Loader.modal();
      const detailUploaded: UploadEncryptedMedia | null =
        await ImagePickerPreviewProvider.uploadMediaChatFile({
          convId: chatSelectedRef.current?.conv_id!,
          mediaFile: file,
          mediaFileName: "image.png",
        });
      Loader.modal(false);

      if (detailUploaded != null) {
        const urlArr: string[] | undefined =
          detailUploaded.file_url?.split(".");
        const fileExt: string = urlArr![urlArr!.length - 1];

        let msg: string = "";

        const msgType: string = imageExtensions.includes(fileExt)
          ? "2"
          : audioExtensions.includes(fileExt)
          ? "3"
          : docExtensions.includes(fileExt)
          ? "6"
          : videoExtensions.includes(fileExt)
          ? "4"
          : pdfExtensions.includes(fileExt)
          ? "7"
          : "8";

        if (msgType === "3") {
          msg = `${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${voiceLengthRef.current}`;
        } else if (msgType === "4") {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${voiceLengthRef.current}`;
        } else if (["6", "7", "8"].includes(msgType)) {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${detailUploaded.original_name}`;
        } else {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}`;
        }

        sendMessage({
          msg: msg,
          msgType: msgType,
          fileId: detailUploaded.file_id,
          callback: () => {
            callback();
          },
        });
      }
    }
  };

  const sendMessage = async ({
    msg,
    msgType,
    fileId = "",
    replyId = "",
    forwardStatus = "0",
    forwardCovId = "",
    forwardKey = "",
    forwardMembers = "",
    forwardChatFlag = "",
    callback,
  }: {
    msg: string;
    msgType: string;
    fileId?: string;
    replyId?: string;
    forwardStatus?: string;
    forwardCovId?: string;
    forwardKey?: string;
    forwardMembers?: string;
    forwardChatFlag?: string;
    callback?: (res?: any) => void;
  }) => {
    if (UtilityHelper.isNotEmpty(msg) && userId !== "0") {
      console.log("sendMessage");
      if (
        UtilityHelper.isNotEmpty(forwardChatFlag) &&
        ["1", "2"].includes(forwardChatFlag) &&
        forwardStatus === "1"
      ) {
        if (forwardChatFlag === "1") {
          const publickKey: string =
            CacheStorage.getValue(StorageKeys.public_key) || "";

          if (msg.length < ChatsProvider.messageMaxLength) {
            const senEncMsg: string | null =
              EncryptDecryptService.encryptWithPublicKey(msg, publickKey);

            const recEncMsg: string | null =
              EncryptDecryptService.encryptWithPublicKey(msg, forwardKey);

            if (senEncMsg != null && recEncMsg != null) {
              const dataMessage: Record<string, string> = {
                [userId]: senEncMsg,
                [forwardMembers]: recEncMsg,
              };

              SocketService.sendTeamMessage({
                flag: chatSelectedRef.current?.flag!,
                convId: forwardCovId,
                message: dataMessage,
                messageType: msgType,
                forwardStatus: forwardStatus,
                fileId: fileId,
                replyId: replyId,
                callback: () => {},
              });
            }
          } else {
            const senEncMsg: HybridEncrypted | null =
              EncryptDecryptService.hybridEncrypt(msg, publickKey);

            const recEncMsg: HybridEncrypted | null =
              EncryptDecryptService.hybridEncrypt(msg, forwardKey);

            if (senEncMsg != null && recEncMsg != null) {
              const dataMessage: Record<string, HybridEncrypted> = {
                [userId]: senEncMsg,
                [forwardMembers]: recEncMsg,
              };

              SocketService.sendTeamMessage({
                flag: chatSelectedRef.current?.flag!,
                convId: forwardCovId,
                message: {},
                hybridMessage: dataMessage,
                messageType: msgType,
                forwardStatus: forwardStatus,
                fileId: fileId,
                replyId: replyId,
                callback: () => {},
              });
            }
          }
        } else if (forwardChatFlag === "2") {
          const encVal: GroupMessageEncryptedType | null =
            await EncryptDecryptService.encryptGroup(forwardKey, msg);

          if (encVal != null) {
            SocketService.sendDeptMessage({
              flag: forwardChatFlag,
              convId: forwardCovId,
              message: encVal.cipherText,
              messageIv: encVal.iv,
              messageTag: encVal.tag,
              messageType: msgType,
              forwardStatus: forwardStatus,
              fileId: fileId,
              replyId: replyId,
              callback: () => {},
            });
          }
        }
      } else {
        const encVal: GroupMessageEncryptedType | null =
          await EncryptDecryptService.encryptGroup(
            forwardChatFlag === "1" ? forwardKey : privateKeyRef.current,
            msg
          );

        if (encVal != null) {
          SocketService.sendGroupMessage({
            flag:
              forwardChatFlag === "1"
                ? forwardChatFlag
                : chatSelectedRef.current?.flag!,
            convId:
              forwardChatFlag === "1"
                ? forwardCovId
                : chatSelectedRef.current?.conv_id!,
            message: encVal.cipherText,
            messageIv: encVal.iv,
            messageTag: encVal.tag,
            messageType: msgType,
            members:
              forwardChatFlag === "1"
                ? forwardMembers
                : conversationDetailRef.current!.members_id!,
            forwardStatus: forwardStatus,
            fileId: fileId,
            replyId: replyId,
            callback: () => {
              if (callback !== undefined) {
                callback();
              }
              clearLongPress();
            },
          });
        }
      }
    }
  };

  const deleteMediaMessages = (flagId: "0" | "1") => {
    if (mediaDeleteEveryoneSelector != null) {
      if ((mediaDeleteEveryoneSelector?.messagesId ?? []).length > 0) {
        selectedMesasgeIdsRef.current = mediaDeleteEveryoneSelector?.messagesId;

        updateDeleteStatus(flagId);

        // REMOVE MODAL
        if (chatSelectedRef.current?.flag !== "3") {
          dispatch(removeModal(ModalKeys.chat_media));
          window.history.replaceState(null, "", window.location.pathname);
        }
      }
    }
  };

  const sendForwardMessageList = async (
    profileConvers: Array<ForwardMessageList>
  ) => {
    console.log("sendForwardMessageList");
    if (forwardedMediaListSelector != null && profileConvers.length > 0) {
      Loader.modal();
      await Promise.all(
        profileConvers.map(async (conv) => {
          forwardedMediaListSelector.map(async (msg) => {
            await sendMessage({
              msg: msg.decripted_mesage!,
              msgType: msg.message_type!,
              fileId: msg.file_id!,
              forwardStatus: "1",
              forwardCovId: conv.conv_id!,
              forwardMembers: conv.account_id!,
              forwardKey: conv.key!,
              forwardChatFlag: conv.flag,
              callback: () => {},
            });
          });
        })
      );
      Loader.modal(false);
    } else {
      if (
        selectedMesasgeIdsRef.current.length > 0 &&
        profileConvers.length > 0
      ) {
        Loader.modal();
        await Promise.all(
          profileConvers.map(async (conv) => {
            selectedMessages.map(async (msg) => {
              /*  if (["1", "5", "9"].includes(msg.message_type!)) {
                await sendMessage({
                  msg: msg.decripted_mesage!,
                  msgType: msg.message_type!,
                  fileId: msg.file_id!,
                  forwardStatus: "1",
                  forwardCovId: conv.conv_id!,
                  forwardMembers: conv.account_id!,
                  forwardKey: conv.key!,
                  forwardChatFlag: conv.flag,
                  callback: () => {},
                });
              } else { */
              await sendMessage({
                msg: msg.decripted_mesage!,
                msgType: msg.message_type!,
                fileId: msg.file_id!,
                forwardStatus: "1",
                forwardCovId: conv.conv_id!,
                forwardKey: conv.key!,
                forwardChatFlag: conv.flag,
                callback: () => {},
              });
              // }
            });
          })
        );
        Loader.modal(false);
      }
    }

    console.log("clear forward");

    UtilityHelper.delay(100);
    dispatch(removeModal(ModalKeys.chat_forward));
    dispatch(removeModal(ModalKeys.chat_forward_media));
    clearLongPress();

    // CLEAR MEDIA SELECTION
    dispatch(mediaDeleteChooseAction(null));
    dispatch(setForwardMediaList(null));

    // RELOAD ONLY IN WEB LAYOUT
    if (type === 3) {
      dispatch(chatRefreshListStatus(true));
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  const updateDeleteStatus = (flagId: "0" | "1") => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      SocketService.deleteMessage({
        flag: chatSelectedRef.current?.flag!,
        convId: chatSelectedRef.current?.conv_id!,
        msgIDs: selectedMesasgeIdsRef.current.join(","),
        status: flagId,
        callback: (response: any) => {
          updateDeleteStatusFromOther(selectedMesasgeIdsRef.current.join(","));
          clearLongPress();
        },
      });
    }
  };
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const handlePressStart = (msdDetail: Message) => {
    timerRef.current = setTimeout(() => {
      longPressStatusRef.current = true;
      setLongPressStatus(true);
      console.log("Long press detected!");

      if (isMobile) {
        toggleMessageIdSelection(msdDetail);
      }
    }, 500);
  };

  const handlePressEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    console.log("Press and hold");
  };

  const handleClickMessage = (msdDetail: Message) => {
    console.log(`handleClickMessage: ${longPressStatusRef.current}`);
    if (longPressStatusRef.current) {
      toggleMessageIdSelection(msdDetail);

      if (selectedMesasgeIdsRef.current.length === 0) {
        clearLongPress();
      }
    }
  };

  const toggleMessageIdSelection = (msdDetail: Message) => {
    if (msdDetail.status === "1" && !msdDetail.deleted?.includes(userId)) {
      let ids = [...selectedMesasgeIdsRef.current];
      const id = msdDetail?.message_id!;

      if (ids.includes(id)) {
        ids = ids.filter((item) => item !== id);
      } else {
        ids.push(id);
      }

      selectedMesasgeIdsRef.current = ids;
      setSelectedMesasgeIds(ids);

      const messsageList: Array<Message> =
        conversationMessagesRef.current!.filter((msg) =>
          ids.includes(msg.message_id!)
        );
      setSelectedMessages(messsageList);
    }
  };

  const longPressAction = async (
    action: "reply" | "copy" | "delete" | "forward"
  ) => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      console.log(`longPressAction: ${action}`);
      if (action === "copy") {
        const messageSelected: Message = selectedMessages[0];
        const chatDecryptedMessage: string =
          messageSelected.decripted_mesage ?? "";

        let copyMsg: string = "";
        switch (messageSelected.message_type) {
          case "1":
          case "9":
            copyMsg = chatDecryptedMessage;
            break;
          case "2":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
          case "3":
            copyMsg = chatDecryptedMessage.split("|")[0];
            break;
          case "4":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
          case "5":
            copyMsg = `https://maps.google.com/?q=${
              chatDecryptedMessage.split("|")[1]
            },${chatDecryptedMessage.split("|")[2]}`;
            break;
          case "6":
          case "7":
          case "8":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
        }

        clearLongPress();

        try {
          await navigator.clipboard.writeText(copyMsg);
          ShowToast.success(AppTranslation.text("text_copied_to_clipboard"));
        } catch (err) {
          ShowToast.error("Failed to copy!");
          console.error("Failed to copy!", err);
        }
      } else if (action === "forward") {
        //clearLongPress(); // keep the mesages
        dispatch(
          addModal({
            id: ModalKeys.chat_forward,
            title: "forward",
            size: "lg",
            screenMobileView: type === 3 ? false : true,
          })
        );
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const disconnectCallBacks = () => {
    if (socket.connected) {
      SocketService.disconnectCallBacks(conversationCallBack);
      console.log(`socket disconnect - GrouphatsContextProvider`);
      //SocketService.disconnect();
      SocketService.leaveChat();
    }
  };

  useEffect(() => {
    if (
      muteDurationGroupSelector != null &&
      UtilityHelper.isNotEmpty(muteDurationGroupSelector)
    ) {
      submitMuteDetail(muteDurationGroupSelector);
    }
    // eslint-disable-next-line
  }, [muteDurationGroupSelector]);

  // DELETE MEDIA
  useEffect(() => {
    if (mediaDeleteActionTypeSelector != null) {
      getSelectedChatSession(false);

      if (chatSelectedRef.current?.flag === "3") {
        deleteMediaMessages(mediaDeleteActionTypeSelector);
      }
      dispatch(mediaDeleteChooseAction(null));
    }
    // eslint-disable-next-line
  }, [mediaDeleteActionTypeSelector]);

  // FORWARD MESSAGES
  useEffect(() => {
    if (forwardedMessagesSelector != null) {
      getSelectedChatSession(false);

      if (chatSelectedRef.current?.flag === "3") {
        sendForwardMessageList(forwardedMessagesSelector);
      }
      dispatch(sendForwardMessages(null));
    }
    // eslint-disable-next-line
  }, [forwardedMessagesSelector]);

  useEffect(() => {
    resetValues();
    // eslint-disable-next-line
  }, []);

  return (
    <GroupChatsContext.Provider
      value={{
        getSelectedChatSession,
        resetMessages,
        conversationCallBack,
        disconnectCallBacks,
        updateConversationSettings,
        submitGroupExit,

        userId,
        conversationMessages,
        conversationDetail,
        muteSettings,

        groupMembers,
        groupMembersOnline,

        removedMember,

        onlineStatus,
        typingStatus,
        recordingStatus,

        // CHATTING
        sendTextMessage,
        sendAudioMessage,
        sendMediaMessage,

        // OPTION
        handleClickMessage,
        handlePressStart,
        handlePressEnd,
        clearLongPress,
        selectedMesasgeIds,
        selectedMessages,
        longPressStatus,

        longPressAction,
      }}
    >
      {children}
    </GroupChatsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const GroupChatsContextImport = () => {
  const context = useContext(GroupChatsContext);
  if (!context) {
    throw new Error(
      "GroupChatsContextImport must be used within a GroupChatsContextProvider"
    );
  }
  return context;
};
